import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { resolveMediaUrl } from "@/lib/imageProxy";
import { UploadCloud, X, Loader2, Star, Camera } from "lucide-react";
import { toast } from "sonner";
import { CameraCapture } from "@/components/CameraCapture";
import { canUseInPageCamera, isMobileDevice } from "@/lib/cameraEnv";

export function DragDropUpload({ images, setImages, testidPrefix = "" }) {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [inPageCamera, setInPageCamera] = useState(false);
  const [mobile, setMobile] = useState(false);
  const inputRef = useRef();
  const nativeCamRef = useRef();
  const tid = (name) => (testidPrefix ? `${testidPrefix}-${name}` : name);

  useEffect(() => {
    setInPageCamera(canUseInPageCamera());
    setMobile(isMobileDevice());
  }, []);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages([...images, ...res.data.urls]);
      toast.success(`${res.data.urls.length} kép feltöltve`);
    } catch (e) {
      toast.error("A feltöltés sikertelen. Próbáld újra.");
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = async (file) => {
    setCameraOpen(false);
    await handleFiles([file]);
  };

  const removeAt = (i) => setImages(images.filter((_, idx) => idx !== i));

  return (
    <div>
      <div
        data-testid={tid("dropzone")}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all ${
          drag ? "border-electric bg-electric/10" : "border-white/15 hover:border-white/30 bg-white/[0.02]"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid={tid("file-input")}
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploading ? (
          <Loader2 className="w-10 h-10 text-electric animate-spin mx-auto" />
        ) : (
          <UploadCloud className="w-10 h-10 text-electric mx-auto" strokeWidth={1.25} />
        )}
        <p className="mt-4 font-heading text-lg">Húzd ide a képeket, vagy kattints a tallózáshoz</p>
        <p className="text-sm text-slate-400 mt-1">JPG, PNG vagy WEBP – több kép egyszerre is</p>
      </div>

      <div className="mt-3 flex flex-wrap justify-center gap-2">
        {/* Telefon: natív Kamera app — nem kell böngészős kamera-engedély */}
        <input
          ref={nativeCamRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid={tid("native-camera-input")}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {(mobile || !inPageCamera) && (
          <button
            type="button"
            data-testid={tid("native-camera")}
            onClick={(e) => {
              e.stopPropagation();
              nativeCamRef.current?.click();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric/15 hover:bg-electric/25 border border-electric/30 text-sm text-white"
          >
            <Camera className="w-4 h-4 text-electric" strokeWidth={1.75} />
            {mobile ? "Fotó (telefon kamerája)" : "Fotó készítése"}
          </button>
        )}
        {inPageCamera && !mobile && (
          <button
            type="button"
            data-testid={tid("open-camera")}
            onClick={(e) => { e.stopPropagation(); setCameraOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-sm text-slate-200"
          >
            <Camera className="w-4 h-4 text-electric" strokeWidth={1.75} />
            Kamera az oldalon
          </button>
        )}
      </div>

      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5" data-testid={tid("uploaded-images")}>
          {images.map((src, i) => (
            <div key={i} className="relative group aspect-[4/3] rounded-xl overflow-hidden border border-white/10 bg-anthracite-900">
              <img
                src={resolveMediaUrl(src)}
                alt={`Feltöltött ${i + 1}. kép`}
                loading="lazy"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
              />
              {i === 0 && (
                <span className="absolute bottom-1 left-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-electric text-white text-[10px]">
                  <Star className="w-2.5 h-2.5" /> Nyitókép
                </span>
              )}
              <button
                type="button"
                data-testid={tid(`remove-image-${i}`)}
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/60 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-all"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
